import { IContact } from '@dataTypes/contact.type'
import { MOCK_CONTACTS } from '@constants/Message/contactMock'
import axios from './axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

// Define the types for the response structure
export type GetAllContactsResponse = {
  statusCode: number
  message: string
  data: {
    contact?: IContact[]
    contacts?: IContact[]
  }
}

type UpdateContactResponse = {
  statusCode: number
  message: string
  data: {
    contact?: IContact[]
  }
}

type GenericResponse = {
  statusCode: number
  message: string
  data: Record<string, unknown>
}

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_CONTACT')
const useMockContactData = getEnv('REACT_APP_USE_CONTACT_MOCK_DATA')

export const enableMockContactData = (): boolean =>
  useMockContactData.trim().toLowerCase() === 'true'

const mockContactsResponse = (): GetAllContactsResponse => ({
  statusCode: 200,
  message: 'OK (mock)',
  data: { contacts: MOCK_CONTACTS, contact: MOCK_CONTACTS },
})

const headers = {
  'Content-Type': 'application/json',
}

const extractContacts = (data: GetAllContactsResponse['data']): IContact[] => {
  if (Array.isArray(data?.contacts)) return data.contacts
  if (Array.isArray(data?.contact)) return data.contact
  return []
}

// Function to get all contacts
export const getAllContacts = async (): Promise<GetAllContactsResponse> => {
  if (enableMockContactData()) {
    return mockContactsResponse()
  }

  try {
    const response = await axios.get<GetAllContactsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/contact/all`,
      {
        headers,
      },
    )

    const contacts = extractContacts(response.data?.data ?? {})
    return {
      ...response.data,
      data: { contacts, contact: contacts },
    }
  } catch (e: unknown) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateContactById = async (
  contact_id: string,
  updatedData: Partial<IContact>,
): Promise<UpdateContactResponse> => {
  try {
    const response = await axios.put<UpdateContactResponse>(
      `${BUSINESS_LAYER_API}/api/v1/contact`,
      { contact_id, data: updatedData },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateContactById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createContact = async (
  contactDetails: IContact,
): Promise<GenericResponse> => {
  try {
    const response = await axios.post<GenericResponse>(
      `${BUSINESS_LAYER_API}/api/v1/contact`,
      contactDetails,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createContact:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Function to delete contact record
export const deleteContactById = async (
  contactId: string,
): Promise<GenericResponse> => {
  try {
    const response = await axios.delete<GenericResponse>(
      `${BUSINESS_LAYER_API}/api/v1/contact/${contactId}`,
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    throw toApiError(e, 'An unknown error occurred')
  }
}
